import { useState, useRef } from 'react';
import { createTalent, checkDuplicateTalent, addEducationToResume, addWorkExperience } from '../../../services/talentService';
import { resumeParseService, ParsedResumeData } from '../../../services/resumeParseService';
import { supabase } from '../../../services/supabase';

interface AddTalentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddTalentModal({ isOpen, onClose, onSuccess }: AddTalentModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    gender: '',
    birth_date: '',
    id_number: '',
    wechat: '',
    linkedin_url: '',
    location: '',
    highest_education: '',
    graduation_school: '',
    major: '',
    graduation_year: '',
    current_company: '',
    current_position: '',
    current_salary: '',
    expected_salary: '',
    years_of_experience: '',
    job_search_status: '主动求职',
    available_date: '',
    preferred_industry: '',
    preferred_position: '',
    preferred_company_type: '',
    work_mode_preference: '',
    overtime_acceptance: '',
    travel_acceptance: '',
    certifications: '',
    self_evaluation: '',
    source: '',
    referrer: '',
    notes: '',
  });

  const [languageSkills, setLanguageSkills] = useState<Array<{ language: string; level: string }>>([
    { language: '英语', level: '' },
  ]);

  const [education, setEducation] = useState<Array<{
    school: string;
    degree: string;
    major: string;
    start_date: string;
    end_date: string;
  }>>([{ school: '', degree: '', major: '', start_date: '', end_date: '' }]);

  const [workExperience, setWorkExperience] = useState<Array<{
    company: string;
    position: string;
    start_date: string;
    end_date: string;
    description: string;
  }>>([{ company: '', position: '', start_date: '', end_date: '', description: '' }]);

  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [currentTab, setCurrentTab] = useState<'basic' | 'education' | 'work' | 'preference'>('basic');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLanguageChange = (index: number, field: 'language' | 'level', value: string) => {
    const newLanguages = [...languageSkills];
    newLanguages[index][field] = value;
    setLanguageSkills(newLanguages);
  };

  const addLanguage = () => {
    setLanguageSkills([...languageSkills, { language: '', level: '' }]);
  };

  const removeLanguage = (index: number) => {
    setLanguageSkills(languageSkills.filter((_, i) => i !== index));
  };

  const handleEducationChange = (index: number, field: string, value: string) => {
    const newEducation = [...education];
    (newEducation[index] as any)[field] = value;
    setEducation(newEducation);
  };

  const addEducation = () => {
    setEducation([...education, { school: '', degree: '', major: '', start_date: '', end_date: '' }]);
  };

  const removeEducation = (index: number) => {
    setEducation(education.filter((_, i) => i !== index));
  };

  const handleWorkChange = (index: number, field: string, value: string) => {
    const newWork = [...workExperience];
    (newWork[index] as any)[field] = value;
    setWorkExperience(newWork);
  };

  const addWork = () => {
    setWorkExperience([...workExperience, { company: '', position: '', start_date: '', end_date: '', description: '' }]);
  };

  const removeWork = (index: number) => {
    setWorkExperience(workExperience.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParsing(true);
    try {
      // 先检查是否有激活的配置
      const config = await resumeParseService.getActiveConfig();
      if (!config) {
        setParsing(false);
        const goToConfig = window.confirm(
          '您还没有配置简历解析接口。\n\n点击"确定"前往管理模块配置，或点击"取消"手动填写人才信息。'
        );
        if (goToConfig) {
          window.REACT_APP_NAVIGATE('/management');
        }
        return;
      }

      const parsedData = await resumeParseService.parseResume(file);
      if (parsedData) {
        fillFormWithParsedData(parsedData);
        alert('简历解析成功！请检查并完善信息');
      }
    } catch (error) {
      console.error('简历解析失败:', error);
      const errorMessage = error instanceof Error ? error.message : '简历解析失败';
      alert(`${errorMessage}\n\n您可以手动填写人才信息，或前往管理模块配置解析接口。`);
    } finally {
      setParsing(false);
      // 清空文件选择，允许重新选择
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fillFormWithParsedData = (data: ParsedResumeData) => {
    setFormData({
      name: data.name || '',
      phone: data.phone || '',
      email: data.email || '',
      gender: data.gender || '',
      birth_date: data.birth_date || '',
      id_number: '',
      wechat: data.wechat || '',
      linkedin_url: data.linkedin_url || '',
      location: data.location || '',
      highest_education: data.highest_education || '',
      graduation_school: data.graduation_school || '',
      major: data.major || '',
      graduation_year: data.graduation_year?.toString() || '',
      current_company: data.current_company || '',
      current_position: data.current_position || '',
      current_salary: data.current_salary || '',
      expected_salary: data.expected_salary || '',
      years_of_experience: data.years_of_experience?.toString() || '',
      job_search_status: data.job_search_status || '主动求职',
      available_date: data.available_date || '',
      preferred_industry: data.preferred_industry?.join(', ') || '',
      preferred_position: data.preferred_position || '',
      preferred_company_type: data.preferred_company_type || '',
      work_mode_preference: data.work_mode_preference || '',
      overtime_acceptance: data.overtime_acceptance || '',
      travel_acceptance: data.travel_acceptance || '',
      certifications: data.certifications?.join(', ') || '',
      self_evaluation: data.self_evaluation || '',
      source: '简历解析',
      referrer: '',
      notes: '',
    });

    if (data.language_skills) {
      const langs = Object.entries(data.language_skills).map(([language, level]) => ({
        language,
        level: level as string,
      }));
      if (langs.length > 0) setLanguageSkills(langs);
    }

    if (data.education && data.education.length > 0) {
      setEducation(data.education);
    }

    if (data.work_experience && data.work_experience.length > 0) {
      setWorkExperience(data.work_experience);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      alert('请填写姓名和手机号');
      return;
    }

    setLoading(true);
    try {
      // 检查重复
      const duplicates = await checkDuplicateTalent(formData.phone, formData.email || undefined);

      if (duplicates.length > 0) {
        const confirm = window.confirm(
          `发现可能重复的人才记录：\n${duplicates.map(d => `${d.name} - ${d.phone}`).join('\n')}\n\n是否继续添加？`
        );
        if (!confirm) {
          setLoading(false);
          return;
        }
      }

      // 处理语言技能
      const languageSkillsObj = languageSkills
        .filter(l => l.language && l.level)
        .reduce((acc, l) => ({ ...acc, [l.language]: l.level }), {});

      // 创建人才 - 将空字符串转换为 undefined
      const talentData = {
        ...formData,
        birth_date: formData.birth_date || undefined,
        available_date: formData.available_date || undefined,
        graduation_year: formData.graduation_year ? parseInt(formData.graduation_year) : undefined,
        years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : undefined,
        preferred_industry: formData.preferred_industry ? formData.preferred_industry.split(',').map(s => s.trim()) : [],
        certifications: formData.certifications ? formData.certifications.split(',').map(s => s.trim()) : [],
        language_skills: Object.keys(languageSkillsObj).length > 0 ? languageSkillsObj : undefined,
        current_status: 'active',
      };

      const newTalent = await createTalent(talentData);

      if (newTalent) {
        // 添加教育经历 - 过滤并转换空日期
        for (const edu of education.filter(e => e.school)) {
          await addEducationToResume(newTalent.id, {
            ...edu,
            start_date: edu.start_date || undefined,
            end_date: edu.end_date || undefined,
          } as any);
        }

        // 添加工作经历 - 过滤并转换空日期
        for (const work of workExperience.filter(w => w.company)) {
          await addWorkExperience(newTalent.id, {
            ...work,
            start_date: work.start_date || undefined,
            end_date: work.end_date || undefined,
          } as any);
        }

        alert('人才添加成功！');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('添加人才失败:', error);
      
      // ✅ 提供更详细的错误信息
      let errorMessage = '添加失败，请重试';
      
      if (error?.message?.includes('row-level security')) {
        errorMessage = '数据库权限错误。\n\n可能原因：\n1. 您的账号没有添加人才的权限\n2. 数据库安全策略配置有误\n\n请联系管理员检查 Supabase RLS 策略配置。';
      } else if (error?.code === '42501') {
        errorMessage = '权限不足，无法添加人才。\n\n请联系管理员检查您的账号权限。';
      } else if (error?.message) {
        errorMessage = `添加失败：${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">添加人才</h2>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              <i className={`ri-file-upload-line ${parsing ? 'animate-pulse' : ''}`}></i>
              {parsing ? '解析中...' : '上传简历解析'}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => setCurrentTab('basic')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              currentTab === 'basic'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            基本信息
          </button>
          <button
            onClick={() => setCurrentTab('education')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              currentTab === 'education'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            教育经历
          </button>
          <button
            onClick={() => setCurrentTab('work')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              currentTab === 'work'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            工作经历
          </button>
          <button
            onClick={() => setCurrentTab('preference')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              currentTab === 'preference'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            求职意向
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {currentTab === 'basic' && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  手机号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">邮箱</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">性别</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">请选择</option>
                  <option value="男">男</option>
                  <option value="女">女</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">出生日期</label>
                <input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => handleInputChange('birth_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">身份证号</label>
                <input
                  type="text"
                  value={formData.id_number}
                  onChange={(e) => handleInputChange('id_number', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">微信号</label>
                <input
                  type="text"
                  value={formData.wechat}
                  onChange={(e) => handleInputChange('wechat', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">所在城市</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">最高学历</label>
                <select
                  value={formData.highest_education}
                  onChange={(e) => handleInputChange('highest_education', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">请选择</option>
                  <option value="高中">高中</option>
                  <option value="大专">大专</option>
                  <option value="本科">本科</option>
                  <option value="硕士">硕士</option>
                  <option value="博士">博士</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">毕业院校</label>
                <input
                  type="text"
                  value={formData.graduation_school}
                  onChange={(e) => handleInputChange('graduation_school', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">专业</label>
                <input
                  type="text"
                  value={formData.major}
                  onChange={(e) => handleInputChange('major', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">毕业年份</label>
                <input
                  type="number"
                  value={formData.graduation_year}
                  onChange={(e) => handleInputChange('graduation_year', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="2020"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">当前公司</label>
                <input
                  type="text"
                  value={formData.current_company}
                  onChange={(e) => handleInputChange('current_company', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">当前职位</label>
                <input
                  type="text"
                  value={formData.current_position}
                  onChange={(e) => handleInputChange('current_position', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">当前薪资</label>
                <input
                  type="text"
                  value={formData.current_salary}
                  onChange={(e) => handleInputChange('current_salary', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="20-30K"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">期望薪资</label>
                <input
                  type="text"
                  value={formData.expected_salary}
                  onChange={(e) => handleInputChange('expected_salary', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="25-35K"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">工作年限</label>
                <input
                  type="number"
                  value={formData.years_of_experience}
                  onChange={(e) => handleInputChange('years_of_experience', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">求职状态</label>
                <select
                  value={formData.job_search_status}
                  onChange={(e) => handleInputChange('job_search_status', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="主动求职">主动求职</option>
                  <option value="被动求职">被动求职</option>
                  <option value="暂不考虑">暂不考虑</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">最快到岗时间</label>
                <input
                  type="date"
                  value={formData.available_date}
                  onChange={(e) => handleInputChange('available_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">技能（逗号分隔）</label>
                <input
                  type="text"
                  value={formData.certifications}
                  onChange={(e) => handleInputChange('certifications', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="PMP, CPA"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">来源渠道</label>
                <input
                  type="text"
                  value={formData.source}
                  onChange={(e) => handleInputChange('source', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="智联招聘、内推等"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">推荐人</label>
                <input
                  type="text"
                  value={formData.referrer}
                  onChange={(e) => handleInputChange('referrer', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">语言能力</label>
                {languageSkills.map((lang, index) => (
                  <div key={index} className="flex gap-3 mb-3">
                    <input
                      type="text"
                      value={lang.language}
                      onChange={(e) => handleLanguageChange(index, 'language', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="语言"
                    />
                    <select
                      value={lang.level}
                      onChange={(e) => handleLanguageChange(index, 'level', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">水平</option>
                      <option value="入门">入门</option>
                      <option value="日常交流">日常交流</option>
                      <option value="熟练">熟练</option>
                      <option value="精通">精通</option>
                      <option value="母语">母语</option>
                    </select>
                    {languageSkills.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLanguage(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addLanguage}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap"
                >
                  + 添加语言
                </button>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">自我评价</label>
                <textarea
                  value={formData.self_evaluation}
                  onChange={(e) => handleInputChange('self_evaluation', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  placeholder="请输入自我评价..."
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  placeholder="其他备注信息..."
                />
              </div>
            </div>
          )}

          {currentTab === 'education' && (
            <div className="space-y-6">
              {education.map((edu, index) => (
                <div key={index} className="p-6 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-gray-900">教育经历 {index + 1}</h3>
                    {education.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEducation(index)}
                        className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        删除
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">学校</label>
                      <input
                        type="text"
                        value={edu.school}
                        onChange={(e) => handleEducationChange(index, 'school', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">学历</label>
                      <select
                        value={edu.degree}
                        onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="">请选择</option>
                        <option value="高中">高中</option>
                        <option value="大专">大专</option>
                        <option value="本科">本科</option>
                        <option value="硕士">硕士</option>
                        <option value="博士">博士</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">专业</label>
                      <input
                        type="text"
                        value={edu.major}
                        onChange={(e) => handleEducationChange(index, 'major', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">开始时间</label>
                      <input
                        type="date"
                        value={edu.start_date}
                        onChange={(e) => handleEducationChange(index, 'start_date', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">结束时间</label>
                      <input
                        type="date"
                        value={edu.end_date}
                        onChange={(e) => handleEducationChange(index, 'end_date', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addEducation}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-teal-600 hover:border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap"
              >
                + 添加教育经历
              </button>
            </div>
          )}

          {currentTab === 'work' && (
            <div className="space-y-6">
              {workExperience.map((work, index) => (
                <div key={index} className="p-6 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-gray-900">工作经历 {index + 1}</h3>
                    {workExperience.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeWork(index)}
                        className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        删除
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">公司</label>
                      <input
                        type="text"
                        value={work.company}
                        onChange={(e) => handleWorkChange(index, 'company', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">职位</label>
                      <input
                        type="text"
                        value={work.position}
                        onChange={(e) => handleWorkChange(index, 'position', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">开始时间</label>
                      <input
                        type="date"
                        value={work.start_date}
                        onChange={(e) => handleWorkChange(index, 'start_date', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">结束时间</label>
                      <input
                        type="date"
                        value={work.end_date}
                        onChange={(e) => handleWorkChange(index, 'end_date', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">工作描述</label>
                      <textarea
                        value={work.description}
                        onChange={(e) => handleWorkChange(index, 'description', e.target.value)}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                        placeholder="工作职责和成就..."
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addWork}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-teal-600 hover:border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap"
              >
                + 添加工作经历
              </button>
            </div>
          )}

          {currentTab === 'preference' && (
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">期望行业（逗号分隔）</label>
                <input
                  type="text"
                  value={formData.preferred_industry}
                  onChange={(e) => handleInputChange('preferred_industry', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="互联网, 金融, 咨询"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">期望职位</label>
                <input
                  type="text"
                  value={formData.preferred_position}
                  onChange={(e) => handleInputChange('preferred_position', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">期望公司类型</label>
                <select
                  value={formData.preferred_company_type}
                  onChange={(e) => handleInputChange('preferred_company_type', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">请选择</option>
                  <option value="外企">外企</option>
                  <option value="国企">国企</option>
                  <option value="民企">民企</option>
                  <option value="创业公司">创业公司</option>
                  <option value="上市公司">上市公司</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">工作模式偏好</label>
                <select
                  value={formData.work_mode_preference}
                  onChange={(e) => handleInputChange('work_mode_preference', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">请选择</option>
                  <option value="全职">全职</option>
                  <option value="兼职">兼职</option>
                  <option value="远程">远程</option>
                  <option value="混合">混合办公</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">加班接受度</label>
                <select
                  value={formData.overtime_acceptance}
                  onChange={(e) => handleInputChange('overtime_acceptance', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">请选择</option>
                  <option value="不接受">不接受</option>
                  <option value="偶尔可以">偶尔可以</option>
                  <option value="可以接受">可以接受</option>
                  <option value="无所谓">无所谓</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">出差接受度</label>
                <select
                  value={formData.travel_acceptance}
                  onChange={(e) => handleInputChange('travel_acceptance', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">请选择</option>
                  <option value="不接受">不接受</option>
                  <option value="短期出差">短期出差</option>
                  <option value="长期出差">长期出差</option>
                  <option value="可以驻外">可以驻外</option>
                </select>
              </div>
            </div>
          )}
        </form>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
